// Mock factories for comparison + survey codes.
// Each factory takes a messageKey (the topic key like 'G1', 'SV1', 'C1', etc.)
// and returns a payload shaped like the real /check response:
//   { message_key, data: { global_variables, result: [ { error_id, ...row, data: [...] } ] } }
//
// Goal: each builder must populate every field the matching render component
// (in app-components/outputs/internal/ or compare/) reads off of
// `row`, `row.data[i]` and `data.global_variables` so the component mounts
// and renders without throwing.

const globalVariables = {
    has_floodway: true,
    has_output: true,
    sel_plan_title: 'Base Plan',
    tar_plan_title: 'Comparison Plan',
    profile: '100yr',
};

const makePayload = (errorId, errorMessage, rowExtras, dataRows) => ({
    data: {
        global_variables: globalVariables,
        result: [{
            error_id: errorId,
            error_message: errorMessage,
            ...rowExtras,
            data: dataRows,
        }],
    },
});

const factory = (errorId, errorMessage, builder) => (messageKey) => {
    const {rowExtras = {}, dataRows = []} = builder() || {};
    const payload = makePayload(errorId, errorMessage, rowExtras, dataRows);
    payload.message_key = messageKey;
    return payload;
};

// ============================================================================
// LIBRARY (G1 / G2 / G6 / G7) -- internal/G*.js
// ============================================================================

// G1 -- LB 01, LB 02
const lb01Builder = () => ({
    rowExtras: {cs_count: 12},
    dataRows: [
        {
            river: 'Main River',
            reach: 'Upper',
            cross_section: 'RS 12345',
            gage_river: 'Main River',
            gage_reach: 'Upper',
        },
        {
            river: 'Main River',
            reach: 'Lower',
            cross_section: 'RS 11000',
            gage_river: 'Main River',
            gage_reach: 'Lower',
        },
    ],
});

const lb02Builder = () => ({
    dataRows: [
        {
            table: [
                ['10yr', 102.31, true],
                ['50yr', 104.12, true],
                ['100yr', 105.46, false],
                ['500yr', 108.74, false],
            ],
        },
    ],
});

// G2 -- LB 03, LB 03 IB
const lb03Builder = () => ({
    rowExtras: {
        tnm_profiles: [2],
        columns: ['10yr', '50yr', '100yr', '500yr'],
    },
    dataRows: [
        {cross_section: 'RS 12345', data: ['102.31', '104.12', '105.46', '108.74']},
        {cross_section: 'RS 12500', data: ['102.55', '104.35', '105.71', '108.95']},
        {cross_section: 'RS 12700', data: ['102.78', '104.59', '105.96', '109.18']},
    ],
});

// G6 -- LB 04, LB 05, LB 06, LB 09
const lb04Builder = () => ({rowExtras: {count: 3}, dataRows: [{}]});
const lb05Builder = () => ({rowExtras: {count: 2}, dataRows: [{}]});

const lb06Builder = () => ({
    rowExtras: {
        tnm_profiles: [1],
        adjusted: [{cross_section: 'RS 12345', col_index: 2}],
        columns: ['10yr', '50yr', '100yr', '500yr'],
    },
    dataRows: [
        {cross_section: 'RS 12345', data: ['102.31', '104.12', '105.46', '108.74']},
        {cross_section: 'RS 12500', data: ['102.55', '104.35', '105.71', '108.95']},
    ],
});

// G7 -- LB 07, LB 08
const lb07Builder = () => ({
    rowExtras: {gage_station: 'RS 11800'},
    dataRows: [
        {
            cross_section: 'RS 12345',
            type: 'Bridge',
            description: 'Main Street Bridge',
            min_high_chord: 110.45,
            min_low_chord: 108.20,
            gage_dist: 545,
            gage_dist_ft: '4',
            warning_item: {profile: '50yr', loc_wsel: 106.45},
            weir_pressure_item: {profile: '100yr', loc_wsel: 108.50},
            footnote: true,
        },
        {
            cross_section: 'RS 12700',
            type: 'Culvert',
            description: 'County Rd Culvert',
            min_high_chord: 109.80,
            min_low_chord: 107.10,
            gage_dist: 900,
            warning_issue: 1,
            weir_pressure_issue: 3,
            footnote: false,
        },
    ],
});

const lb08Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            type: 'Bridge',
            min_high_chord: 110.45,
            min_low_chord: 108.20,
            profiles: [
                {profile: '10yr',  loc_wsel: 102.31, freeboard: 5.89, status: 'Normal'},
                {profile: '50yr',  loc_wsel: 106.45, freeboard: 1.75, status: 'Warning'},
                {profile: '100yr', loc_wsel: 108.50, freeboard: -0.30, status: 'Pressure/Weir'},
            ],
        },
    ],
});

// ============================================================================
// SURVEY (SV*) -- internal/SV*.js
// ============================================================================

const sv01Builder = () => ({
    dataRows: [
        {culvert_num: 1, cross_section: 'RS 12345', survey_us: 100.50, culv_us: 100.75, survey_ds: 99.80, culv_ds: 99.95},
        {culvert_num: 2, cross_section: 'RS 12700', survey_us: 98.20, culv_us: 98.20, survey_ds: 97.40, culv_ds: 97.55},
    ],
});

const sv02Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            us_l_survey: 110.20, us_l: 110.40,
            us_r_survey: 110.25, us_r: 110.25,
            ds_l_survey: 110.10, ds_l: 110.30,
            ds_r_survey: 110.15, ds_r: 110.15,
        },
        {
            cross_section: 'RS 12700',
            us_l_survey: 108.50, us_l: 108.50,
            us_r_survey: 108.60, us_r: 108.80,
            ds_l_survey: 108.40, ds_l: 108.40,
            ds_r_survey: 108.55, ds_r: 108.75,
        },
    ],
});

const sv03Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345', left_elev_survey: 105.20, left_elev: 105.40, right_elev_survey: 105.30, right_elev: 105.30},
        {cross_section: 'RS 12700', left_elev_survey: 104.10, left_elev: 104.10, right_elev_survey: 104.25, right_elev: 104.50},
    ],
});

const sv04Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            culvert_num: 1,
            culuscr: 103.50, culusinv: 100.50, rise_us: 3.00,
            culdscr: 102.80, culdsinv: 99.80, rise_ds: 3.00,
            model_rise: 3.25,
        },
        {
            cross_section: 'RS 12700',
            culvert_num: 2,
            culuscr: 101.20, culusinv: 98.20, rise_us: 3.00,
            culdscr: 100.40, culdsinv: 97.40, rise_ds: 3.00,
            model_rise: 3.00,
        },
    ],
});

const sv05Builder = () => ({
    dataRows: [
        {culvert_num: 1, cross_section: 'RS 12345', survey_len: 45.20, culv_len: 45.50},
        {culvert_num: 2, cross_section: 'RS 12700', survey_len: 60.00, culv_len: 62.50},
    ],
});

const sv06Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345', left: 40.20, right: 40.50, bridge_width: 40.00},
        {cross_section: 'RS 12700', left: 35.10, right: 35.30, bridge_width: 36.00},
    ],
});

const sv07Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345'},
        {cross_section: 'RS 12700'},
    ],
});

// ============================================================================
// COMPARE (C1..C9, CFL2, COP2, CFW2)
// ============================================================================

// C1 -- NR-01, NR-02, NR-ICS-01, NR-ICS-02, C-XS 04, C-XS 05
const csListBuilder = () => ({
    dataRows: [
        {cross_section: 'RS 12345'},
        {cross_section: 'RS 12500'},
        {cross_section: 'RS 12700'},
    ],
});

const cxs05Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            base: {cross_section: 'RS 12360', lob: 0.08, chn: 0.035, rob: 0.08},
            cmp:  {lob: 0.10, chn: 0.040, rob: 0.09},
        },
        {
            cross_section: 'RS 12500',
            base: {cross_section: 'RS 12520', lob: 0.07, chn: 0.030, rob: 0.07},
            cmp:  {lob: 0.07, chn: 0.030, rob: 0.07},
        },
    ],
});

const cxs04Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345', assoc_cross_section: 'RS 12347'},
        {cross_section: 'RS 12500', assoc_cross_section: 'RS 12503'},
    ],
});

// C2 -- C-XS 02, C-XS 02 IB, IS 03
const cxs02Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345'},
        {cross_section: 'RS 12500'},
        {cross_section: 'RS 12700'},
    ],
});

const is03Builder = () => ({
    dataRows: [
        {type: 'out_missing', missing_sides: ['base']},
        {type: 'xs_missing', cross_section: 'RS 12345', missing_sides: ['base']},
        {type: 'xs_missing', cross_section: 'RS 12500', missing_sides: ['comparison']},
        {type: 'area_diff', cross_section: 'RS 12700', profile: '10yr',  diff: '3.2%'},
        {type: 'area_diff', cross_section: 'RS 12700', profile: '100yr', diff: '5.7%'},
        {type: 'area_diff', cross_section: 'RS 12800', profile: '10yr',  diff: '2.1%'},
        {type: 'area_diff', cross_section: 'RS 12800', profile: '100yr', diff: '4.5%'},
    ],
});

// C3 -- NC-01..NC-06, C-ST 07, C-ST 08
const ncBuilder = () => ({
    dataRows: [
        {cross_section: 'RS 12345', description: 'Main Street Crossing'},
        {cross_section: 'RS 12700', description: 'County Rd Crossing'},
    ],
});

const cst07Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345', assoc_cross_section: 'RS 12347', type: 'Bridge'},
        {cross_section: 'RS 12700', assoc_cross_section: 'RS 12705', type: 'Culvert'},
    ],
});

// C4 -- C-ST 04, C-ST 05
const tcd01Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            type: 'Bridge',
            deck_dist_diff: [
                {name: 'Width', base: 40, compare: 42},
                {name: 'Skew', base: 0, compare: 5},
            ],
            culvert_diff: {
                has_diff: true,
                added:   [{name: 'C2', plan: 'Comparison'}],
                removed: [{name: 'C3', plan: 'Base'}],
                diff:    [{name: 'C1', diffs: [{name: 'Rise', base: 3.0, compare: 3.5}]}],
            },
            pier_diff: {
                has_diff: true,
                added:   [{name: 'P3', plan: 'Comparison'}],
                removed: [],
                diff:    [{name: 'P1', diffs: [{name: 'Width', base: 2.0, compare: 2.5}]}],
            },
            abutment_diff: {
                has_diff: true,
                added:   [],
                removed: [{name: 'A2', plan: 'Base'}],
                diff:    [{name: 'A1', diffs: [{name: 'Slope', base: 2, compare: 3}]}],
            },
        },
    ],
});

const tcd02Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            weir_embank_diff: [
                {name: 'Crest Elev', base: 110.00, compare: 110.50},
                {name: 'Weir Coef', base: 2.6, compare: 2.8},
            ],
            culvert_diff: {
                has_diff: true,
                added:   [{name: 'C2', plan: 'Comparison'}],
                removed: [{name: 'C3', plan: 'Base'}],
                diff:    [{culvert: 'C1', diffs: [{name: 'Rise', base: 3.0, compare: 3.5}]}],
            },
        },
    ],
});

// C5 -- BMC-01
const bmc01Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            diffs: [
                {
                    name: 'Approach 1',
                    low_flow:    [{name: 'Energy',  base: '*', compare: '*'}],
                    low_flow_ws: [{name: 'WS Pro',  base: 'Y', compare: 'N'}],
                    high_flow:   [{name: 'Pressure', base: 'Y', compare: 'N'}],
                    removed_bridges: [{name: 'Old Br', base: 'Y', compare: ''}],
                },
            ],
        },
    ],
});

// C6 -- T-M-01, T-M-ICS-01
const mannBuilder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            base_chn_stations: [100],
            cmp_chn_stations: [100],
            matched_table: [
                {number: 1, base_station: 0,   base_val: 0.08, cmp_station: 0,   cmp_val: 0.09},
                {number: 2, base_station: 100, base_val: 0.035, cmp_station: 100, cmp_val: 0.035},
                {number: 3, base_station: 200, base_val: 0.08, cmp_station: 200, cmp_val: 0.10},
            ],
        },
    ],
});

// C7 -- C-FW 00-A, C-FW 00-B, C-FW 01, T-TopW-01
const cfw00aBuilder = () => ({
    dataRows: [
        {type: 'only_in_base', cross_section: 'RS 12345'},
        {type: 'only_in_comparison', cross_section: 'RS 12700'},
    ],
});

const cfw00bBuilder = () => ({
    dataRows: [
        {type: 'base', cross_section: 'RS 12345'},
        {type: 'comparison', cross_section: 'RS 12700'},
    ],
});

const cfw01Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345', base_left: 120.5, base_right: 480.5, compare_left: 125.0, compare_right: 478.0},
        {cross_section: 'RS 12700', base_left: 130.0, base_right: 490.0, compare_left: 130.0, compare_right: 495.0},
    ],
});

const ttopw01Builder = () => ({
    dataRows: [
        {
            cross_section: 'RS 12345',
            base:    {sel_topW_L: 60, sel_topW_R: 70, sel_topW: 130},
            compare: {tar_topW_L: 65, tar_topW_R: 70, tar_topW: 135},
            diff: 5,
        },
        {
            cross_section: 'RS 12700',
            base:    {sel_topW_L: 55, sel_topW_R: 65, sel_topW: 120},
            compare: {tar_topW_L: 55, tar_topW_R: 60, tar_topW: 115},
            diff: -5,
        },
    ],
});

// C8 -- C-XY 01
const cxy01Builder = () => ({
    dataRows: [
        {cross_section: 'RS 12345'},
        {cross_section: 'RS 12700'},
    ],
});

// C9 -- C-XY 02
const cxy02Builder = () => ({
    dataRows: [
        {ds_cross_section: 'RS 12345', us_cross_section: 'RS 12500', diff: 12.5},
        {ds_cross_section: 'RS 12500', us_cross_section: 'RS 12700', diff: -3.2},
    ],
});

// CFL2 (also satisfies C10's CFL02 case) -- C-FL 02
const cfl02Builder = () => ({
    dataRows: [
        {
            profile_name: '100yr',
            base_fregime: 'Subcritical',
            comp_fregime: 'Mixed',
            base: {dn_text: 'Normal Depth', dn_value: '0.005'},
            cmp:  {dn_text: 'Known WS', dn_value: '105.5'},
        },
        {
            profile_name: '500yr',
            base_fregime: 'Subcritical',
            comp_fregime: 'Subcritical',
            base: {dn_text: 'Normal Depth', dn_value: '0.005'},
            cmp:  {dn_text: 'Normal Depth', dn_value: '0.005'},
        },
    ],
});

// COP2 -- C-OP 02
const cop02Builder = () => ({
    rowExtras: {
        cs_count: 3,
        start_cs: 'RS 12345',
        end_cs: 'RS 12800',
    },
    dataRows: [
        {cross_section: 'RS 12345', diff: 0,    selected_ws_elev: 105.46, target_ws_elev: 105.46, type: 'Tie-in', desc: 'US limit'},
        {cross_section: 'RS 12500', diff: 0.55, selected_ws_elev: 104.20, target_ws_elev: 104.75},
        {cross_section: 'RS 12700', diff: -0.30, selected_ws_elev: 102.80, target_ws_elev: 102.50},
        {cross_section: 'RS 12800', diff: 0,    selected_ws_elev: 101.00, target_ws_elev: 101.00, type: 'Tie-in', desc: 'DS limit'},
    ],
});

// CFW2 -- C-FW 02 (also keyed C-FW 03..07)
const cfw02Builder = () => ({
    dataRows: [
        {label: 'WSEL Tolerance',           base_value: '0.01', comp_value: '0.02'},
        {label: 'Critical Depth Tolerance', base_value: '0.01', comp_value: '0.01'},
        {label: 'Max Iterations',           base_value: '20',   comp_value: '40'},
    ],
});

export const compareSurveyMocks = {
    // --- Library (G*) --------------------------------------------------------
    'LB 01': factory('LB 01', 'Library cross-section / gage location summary.', lb01Builder),
    'LB 02': factory('LB 02', 'Library model WSEL targets per profile.', lb02Builder),
    'LB 03': factory('LB 03', 'Library model WSEL table.', lb03Builder),
    'LB 03 IB': factory('LB 03 IB', 'Library model WSEL table at internal bridge cross sections.', lb03Builder),
    'LB 04': factory('LB 04', 'WSEL values adjusted to prevent choking cross sections.', lb04Builder),
    'LB 05': factory('LB 05', 'WSEL values adjusted to prevent crossing profiles.', lb05Builder),
    'LB 06': factory('LB 06', 'Processed (interpolated, choking and crossing adjusted) library WSEL table.', lb06Builder),
    'LB 09': factory('LB 09', 'Processed library WSEL table after additional adjustment.', lb06Builder),
    'LB 07': factory('LB 07', 'Inundation thresholds table for library structures.', lb07Builder),
    'LB 08': factory('LB 08', 'Per-structure inundation table.', lb08Builder),

    // --- Survey (SV*) --------------------------------------------------------
    'SV 01': factory('SV 01', 'Culvert invert elevations not consistent with survey data.', sv01Builder),
    'SV 02': factory('SV 02', 'Bridge low chord elevations not consistent with survey data.', sv02Builder),
    'SV 03': factory('SV 03', 'Top of bank elevations not consistent with survey data.', sv03Builder),
    'SV 04': factory('SV 04', 'Culvert rise not consistent with survey data.', sv04Builder),
    'SV 05': factory('SV 05', 'Culvert length not consistent with survey data.', sv05Builder),
    'SV 06': factory('SV 06', 'Bridge width not consistent with survey data.', sv06Builder),
    'SV 07': factory('SV 07', 'Stream centerline is not between TOBL/TOBR survey points.', sv07Builder),

    // --- Compare C1 ----------------------------------------------------------
    'NR-01':     factory('NR-01',     'Cross sections removed.', csListBuilder),
    'NR-02':     factory('NR-02',     'New cross sections added.', csListBuilder),
    'NR-ICS-01': factory('NR-ICS-01', 'Internal bridge cross sections removed.', csListBuilder),
    'NR-ICS-02': factory('NR-ICS-02', 'New internal bridge cross sections added.', csListBuilder),
    'C-XS 05':   factory('C-XS 05',   "Conveyance-weighted Manning's n differences vs. immediate US base cross section.", cxs05Builder),
    'C-XS 04':   factory('C-XS 04',   'Cross-section river stations revised due to stream centerline realignment.', cxs04Builder),

    // --- Compare C2 ----------------------------------------------------------
    'C-XS 02':    factory('C-XS 02',    'Topography differences in Station/Elevation data.', cxs02Builder),
    'IS 03':      factory('IS 03',      'Cross-section flow area differences across profiles.', is03Builder),
    'C-XS 02 IB': factory('C-XS 02 IB', 'Topography differences at internal bridge cross sections.', cxs02Builder),

    // --- Compare C3 ----------------------------------------------------------
    'NC-01': factory('NC-01', 'Culverts added.', ncBuilder),
    'NC-02': factory('NC-02', 'Culverts removed.', ncBuilder),
    'NC-03': factory('NC-03', 'Bridges added.', ncBuilder),
    'NC-04': factory('NC-04', 'Bridges removed.', ncBuilder),
    'NC-05': factory('NC-05', 'Inline structures added.', ncBuilder),
    'NC-06': factory('NC-06', 'Inline structures removed.', ncBuilder),
    'C-ST 07': factory('C-ST 07', 'Structures revised due to replacement within proximity.', cst07Builder),
    'C-ST 08': factory('C-ST 08', 'Structures revised due to stream centerline realignment.', cst07Builder),

    // --- Compare C4 ----------------------------------------------------------
    'C-ST 04': factory('C-ST 04', 'Differences in bridge/culvert structure geometry.', tcd01Builder),
    'C-ST 05': factory('C-ST 05', 'Differences in inline structure geometry.', tcd02Builder),

    // --- Compare C5 ----------------------------------------------------------
    'BMC-01': factory('BMC-01', 'Differences in bridge modeling approach.', bmc01Builder),

    // --- Compare C6 ----------------------------------------------------------
    'T-M-01':     factory('T-M-01',     "Differences in Manning's n value at cross sections.", mannBuilder),
    'T-M-ICS-01': factory('T-M-ICS-01', "Differences in Manning's n value at internal bridge cross sections.", mannBuilder),

    // --- Compare C7 ----------------------------------------------------------
    'C-FW 00-A': factory('C-FW 00-A', 'Cross sections missing in listed plans for floodway compare.', cfw00aBuilder),
    'C-FW 00-B': factory('C-FW 00-B', 'Cross sections lacking defined encroachment stations.', cfw00bBuilder),
    'C-FW 01':   factory('C-FW 01',   'Differences in encroachment station placements.', cfw01Builder),
    'T-TopW-01': factory('T-TopW-01', 'Differences in encroachment top width.', ttopw01Builder),

    // --- Compare C8 / C9 -----------------------------------------------------
    'C-XY 01': factory('C-XY 01', 'Differences in cutlines (Schematic X, Y).', cxy01Builder),
    'C-XY 02': factory('C-XY 02', 'Differences in stream centerline Schematic X, Y.', cxy02Builder),

    // --- Compare CFL2 / COP2 / CFW2 -----------------------------------------
    'C-FL 02': factory('C-FL 02', 'Flow regime / boundary condition changes.', cfl02Builder),
    'C-OP 02': factory('C-OP 02', 'Differences in FW WSEL between base and comparison plans.', cop02Builder),
    'C-FW 02': factory('C-FW 02', 'HEC-RAS calculation tolerance differences.', cfw02Builder),
};
